/**
 * `FontStore` implementation backed by the browser IndexedDB API.
 *
 * The raw IndexedDB API is used directly (no `idb` / `dexie`) to keep
 * the `apps/web` bundle free of additional runtime dependencies — the
 * verbosity is isolated here, behind a small async surface.
 *
 * ### Schema
 *
 *   Database:     `"edit-dwg"` (version 1)
 *   Object store: `"shx-fonts"`
 *     - key path: out-of-line (key supplied explicitly on each `put`)
 *     - value:    `Uint8Array` of the raw `.shx` file bytes
 *     - key:      `normalizeFontKey(name)` — always lower-case, suffixless
 *
 * Future migrations add new stores via `onupgradeneeded` by bumping
 * `DB_VERSION`; existing data is preserved because the `shx-fonts`
 * store is never dropped.
 *
 * ### Cancellation
 *
 * Every operation accepts an `AbortSignal`. When the signal fires, the
 * active transaction is aborted via `IDBTransaction.abort()` and the
 * pending promise rejects with a standard `DOMException("AbortError")`.
 * This satisfies the `FontStore` interface contract.
 *
 * ### Error handling
 *
 * Transient failures (quota exceeded, connection dropped) reject with
 * the underlying `DOMException`. The higher-level `hydrateFontsFromStore`
 * catches and logs, but does not crash the app — the registry stays
 * empty in that case and fonts fall back to the system `<text>`
 * renderer.
 */

import {
  FontStoreUnavailableError,
  normalizeFontKey,
  type FontStore,
} from "./fontStore";

const DB_NAME = "edit-dwg";
const DB_VERSION = 1;
const STORE_NAME = "shx-fonts";

/**
 * Returns `true` when the host environment exposes a usable
 * `indexedDB` global. Kept as a separate helper so tests can stub
 * either the global or the constructor's availability check without
 * monkey-patching `window`.
 */
function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

/**
 * Wrap an `IDBRequest` in a Promise that also listens to an optional
 * `AbortSignal`. When the signal aborts mid-request, the surrounding
 * transaction is torn down and the promise rejects with an AbortError.
 */
function requestAsPromise<T>(
  request: IDBRequest<T>,
  transaction: IDBTransaction,
  signal: AbortSignal | undefined
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let abortHandler: (() => void) | null = null;

    const cleanup = (): void => {
      if (abortHandler && signal) {
        signal.removeEventListener("abort", abortHandler);
      }
    };

    request.onsuccess = () => {
      cleanup();
      resolve(request.result);
    };
    request.onerror = () => {
      cleanup();
      reject(request.error ?? new Error("IndexedDB request failed"));
    };

    if (signal) {
      if (signal.aborted) {
        try {
          transaction.abort();
        } catch {
          // transaction may already be finished — ignore
        }
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      abortHandler = () => {
        try {
          transaction.abort();
        } catch {
          // ignore
        }
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
      };
      signal.addEventListener("abort", abortHandler, { once: true });
    }
  });
}

/**
 * Await the completion of an `IDBTransaction`. Required after the last
 * request of a write transaction so the promise settles only once the
 * transaction has actually committed (vs. just queued).
 */
function transactionComplete(
  transaction: IDBTransaction,
  signal: AbortSignal | undefined
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let abortHandler: (() => void) | null = null;
    const cleanup = (): void => {
      if (abortHandler && signal) {
        signal.removeEventListener("abort", abortHandler);
      }
    };

    transaction.oncomplete = () => {
      cleanup();
      resolve();
    };
    transaction.onerror = () => {
      cleanup();
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    };
    transaction.onabort = () => {
      cleanup();
      reject(
        transaction.error ?? new DOMException("Aborted", "AbortError")
      );
    };

    if (signal) {
      if (signal.aborted) {
        try {
          transaction.abort();
        } catch {
          // ignore
        }
        return;
      }
      abortHandler = () => {
        try {
          transaction.abort();
        } catch {
          // ignore
        }
      };
      signal.addEventListener("abort", abortHandler, { once: true });
    }
  });
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

export class IndexedDbFontStore implements FontStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor() {
    if (!isIndexedDbAvailable()) {
      throw new FontStoreUnavailableError(
        "IndexedDB is not available in this environment"
      );
    }
  }

  /**
   * Lazily open / upgrade the database. Shared across all operations
   * so we keep a single connection — IndexedDB handles concurrent
   * transactions internally, and repeated `open()` calls are a known
   * Firefox performance trap.
   */
  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // Out-of-line keys: we pass the key explicitly on every
          // put(). This matches the plan — the "logical" key is the
          // normalised font name, not a field inside the stored
          // bytes.
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // If something else triggers a version-change (e.g. the user
        // opens a second tab), close gracefully so the other
        // connection can upgrade.
        db.onversionchange = () => {
          db.close();
          this.dbPromise = null;
        };
        resolve(db);
      };
      request.onerror = () => {
        reject(request.error ?? new Error("indexedDB.open failed"));
      };
      request.onblocked = () => {
        reject(
          new Error("IndexedDB open blocked by another connection")
        );
      };
    });
    return this.dbPromise;
  }

  async save(
    name: string,
    bytes: Uint8Array,
    signal?: AbortSignal
  ): Promise<void> {
    throwIfAborted(signal);
    const key = normalizeFontKey(name);
    const db = await this.open();
    throwIfAborted(signal);

    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    // Clone into a fresh Uint8Array so the stored value cannot be
    // mutated by the caller after the put returns (IDB normally
    // serialises structured clones on commit, but copying up-front
    // makes the test expectations deterministic in fake-indexeddb
    // which reuses the same buffer object).
    const snapshot = new Uint8Array(bytes);
    const request = store.put(snapshot, key);

    await requestAsPromise(request, tx, signal);
    await transactionComplete(tx, signal);
  }

  async loadAll(
    signal?: AbortSignal
  ): Promise<Array<{ name: string; bytes: Uint8Array }>> {
    throwIfAborted(signal);
    const db = await this.open();
    throwIfAborted(signal);

    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    const keysRequest = store.getAllKeys();
    const valuesRequest = store.getAll();

    const [keys, values] = await Promise.all([
      requestAsPromise(keysRequest, tx, signal),
      requestAsPromise(valuesRequest, tx, signal),
    ]);

    await transactionComplete(tx, signal);

    const result: Array<{ name: string; bytes: Uint8Array }> = [];
    for (let i = 0; i < keys.length; i++) {
      const rawKey = keys[i];
      const rawValue = values[i];
      if (typeof rawKey !== "string") continue;
      if (rawValue instanceof Uint8Array) {
        result.push({ name: rawKey, bytes: rawValue });
      } else if (rawValue instanceof ArrayBuffer) {
        // Defensive: older browsers that don't preserve Uint8Array
        // identity across IDB serialisation would return ArrayBuffer.
        result.push({ name: rawKey, bytes: new Uint8Array(rawValue) });
      }
    }
    return result;
  }

  async remove(name: string, signal?: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    const key = normalizeFontKey(name);
    const db = await this.open();
    throwIfAborted(signal);

    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(key);
    await requestAsPromise(request, tx, signal);
    await transactionComplete(tx, signal);
  }

  async clear(signal?: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    const db = await this.open();
    throwIfAborted(signal);

    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.clear();
    await requestAsPromise(request, tx, signal);
    await transactionComplete(tx, signal);
  }

  /**
   * Diagnostic-only: close the underlying connection and reset the
   * lazy promise so the next operation reopens. Used by tests that
   * need to simulate tab-close / reload cycles.
   */
  close(): void {
    if (this.dbPromise) {
      void this.dbPromise.then(
        (db) => db.close(),
        () => undefined
      );
      this.dbPromise = null;
    }
  }
}
